import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { Radio, Menu } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { SupportButton } from "@/components/SupportPanel";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SidebarContent } from "@/components/layout/Sidebar";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { findNavItem } from "@/components/layout/nav";
import { CommandPalette, CommandPaletteButton } from "@/components/layout/CommandPalette";
import { UnsavedBar } from "@/components/UnsavedBar";
import { confirmDiscard, unsavedStore } from "@/lib/unsaved";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const GuildCtx = createContext(null);
export const useGuild = () => useContext(GuildCtx);


export default function DashboardLayout() {
  const { guildId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [guilds, setGuilds] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const doLogout = () => { if (!confirmDiscard()) return; logout(); navigate("/"); };
  const current = findNavItem(location.pathname.split("/").slice(4).join("/"));
  useEffect(() => { api.get("/guilds").then((r) => setGuilds(r.data)).catch(() => setGuilds([])); }, []);
  const [guild, setGuild] = useState(null);
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(false);
  const wsRef = useRef(null);
  const timer = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [botStatus, setBotStatus] = useState(null);

  const loadGuild = () => api.get(`/guilds/${guildId}`).then((r) => setGuild(r.data)).catch(() => navigate("/app"));
  useEffect(() => { loadGuild(); }, [guildId]);

  useEffect(() => {
    let active = true;
    const check = () => api.get("/bot/status").then((r) => active && setBotStatus(r.data)).catch(() => {});
    check();
    const id = setInterval(check, 20000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const pushEvents = (evts) => {
    if (evts?.length) { setEvents((prev) => [...evts, ...prev].slice(0, 30)); if (!unsavedStore.get().dirty) setRefreshKey((k) => k + 1); }
  };

  const startPolling = () => {
    timer.current = setInterval(async () => {
      try { const { data } = await api.post(`/guilds/${guildId}/simulate`); pushEvents(data.events); } catch (e) {}
    }, 3500);
  };

  const stopLive = () => {
    if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); wsRef.current = null; }
    clearInterval(timer.current);
    setLive(false);
  };

  const toggleLive = () => {
    if (live) { stopLive(); return; }
    setLive(true);
    const token = localStorage.getItem("crank_token");
    const wsBase = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    try {
      const ws = new WebSocket(`${wsBase}/api/ws/live/${guildId}?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => toast.success("Live stream connected (WebSocket)", { duration: 1500 });
      ws.onmessage = (msg) => {
        const e = JSON.parse(msg.data);
        if (e.type === "connected") return;
        pushEvents([e]);
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        if (wsRef.current) { wsRef.current = null; toast.message("WebSocket unavailable — using live polling"); startPolling(); }
      };
    } catch (e) { startPolling(); }
  };
  useEffect(() => () => stopLive(), []);

  return (
    <GuildCtx.Provider value={{ guild, guildId, reloadGuild: loadGuild, refreshKey, events }}>
      <div className="flex h-screen overflow-hidden bg-black">
        <aside className="hidden lg:flex w-64 border-r border-zinc-800 bg-black flex-col shrink-0" data-testid="dashboard-sidebar">
          <SidebarContent guild={guild} guilds={guilds} onLogout={doLogout} />
        </aside>
        <CommandPalette open={palette} setOpen={setPalette} guildId={guildId} onLogout={doLogout} />
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetContent side="left" className="p-0 w-[280px] bg-black border-zinc-800" data-testid="mobile-nav-drawer">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent guild={guild} guilds={guilds} onLogout={doLogout} onNavigate={() => setDrawer(false)} />
          </SheetContent>
        </Sheet>

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <header className="h-16 border-b border-zinc-800 flex items-center justify-between gap-3 px-4 sm:px-6 shrink-0 bg-black/80 backdrop-blur-md z-30">
            <div className="flex items-center gap-3 min-w-0">
              <button data-testid="mobile-nav-open" aria-label="Open navigation" onClick={() => setDrawer(true)}
                className="lg:hidden w-9 h-9 rounded-lg border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-900 shrink-0">
                <Menu className="w-4 h-4" />
              </button>
              <div className="min-w-0">
                <div className="hidden sm:block text-[11px] uppercase tracking-[0.14em] text-zinc-500 truncate" data-testid="topbar-breadcrumb">{guild?.name || "…"}{current ? ` · ${current.group.label}` : ""}</div>
                <div className="font-head font-semibold truncate max-w-[110px] sm:max-w-none" data-testid="topbar-title">{current?.item.label || "Dashboard"}</div>
              </div>
              <div data-testid="bot-status-badge" title={botStatus?.bot_user ? `${botStatus.bot_user} · ${botStatus.guild_count} guilds` : "Bot heartbeat"}
                className={`flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 font-medium transition-colors shrink-0 ${botStatus?.online ? "bg-emerald-500/10 text-emerald-500" : "bg-zinc-800 text-zinc-400"}`}>
                <span className={`w-2 h-2 rounded-full ${botStatus?.online ? "bg-emerald-500 animate-pulse" : "bg-zinc-500"}`} />
                <span className="hidden md:inline">{botStatus?.online ? "Bot Online" : "Bot Offline"}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <CommandPaletteButton onClick={() => setPalette(true)} />
              <SupportButton />
              <NotificationBell />
              <button data-testid="live-toggle" onClick={toggleLive} aria-pressed={live} className={`hidden sm:flex items-center gap-2 text-sm rounded-lg px-3.5 h-9 font-medium transition-colors ${live ? "bg-volt text-volt-ink" : "border border-zinc-800 text-zinc-300 hover:bg-zinc-900"}`}>
                <Radio className={`w-4 h-4 ${live ? "live-dot" : ""}`} /> {live ? "Live" : "Go Live"}
              </button>
              <ThemeToggle className="hidden sm:flex" />
              <AccountMenu guildId={guildId} onLogout={doLogout} />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto bg-zinc-950">
            <div key={location.pathname} className="page-enter max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
              <Outlet />
            </div>
            <UnsavedBar />
          </main>
        </div>

        {events.length > 0 && (
          <aside className="w-72 border-l border-zinc-800 bg-black flex-col shrink-0 hidden xl:flex">
            <div className="h-16 border-b border-zinc-800 flex items-center px-4 text-xs font-bold uppercase tracking-widest text-zinc-400"><Radio className="w-4 h-4 mr-2 text-volt" /> Live Feed</div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {events.map((e, i) => (
                <div key={i} data-testid="live-event" className="text-sm bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 animate-in">
                  <span className={`font-semibold ${e.type === "moderation" ? "text-red-400" : e.type === "level_up" ? "text-[#f59e0b]" : e.type === "join" ? "text-volt" : "text-zinc-300"}`}>{e.user}</span>
                  <span className="text-zinc-400"> {e.detail}</span>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
    </GuildCtx.Provider>
  );
}
